import { useEffect, useRef, useState } from "react";
import { Mention, MentionsInput } from "react-mentions";
import { useParams } from "react-router-dom";

import styled, { css, keyframes } from "styled-components";

import "./PDFChatInputStyle.css";

import AiCursor from "components/atoms/AiCursor";
import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import Confirm from "components/atoms/alert/Confirm";
import Button from "components/atoms/button/Button";
import Text from "components/atoms/text/Text";

import Constants from "utils/constants";
import { hasPrepMention, matchMention, mentionCandidates } from "utils/mention";
import { genericEnterKeyDown } from "utils/validation";

const ThreadContainer = styled.div`
    position: relative;

    margin: 8px;
    padding: 8px;
    width: calc(100% - 16px);
    height: auto;
    background-color: ${({ bgColor }) => (bgColor ? bgColor : "var(--color-White)")};

    border: solid 1px var(--color-Outline);
    border-radius: 4px;
    box-shadow: 1px 1px 8px rgba(0, 0, 0, 0.1);
    transition: background-color 80ms ease-in;

    cursor: pointer;
    &:hover {
        background-color: var(--color-ListHover);
    }
`;

const ThreadHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const ThreadHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 0px;
`;

const ThreadHeaderRight = styled.div`
    display: flex;
    align-items: center;
    gap: 0px;
`;

const ThreadMessageCountText = styled(Text)`
    padding: 2px 4px;

    font-size: 1.2rem;
    color: var(--color-White);
    background-color: var(--color-Warn);
    border-radius: 2px;
`;

const ThreadTitle = styled(Text)`
    font-size: 1.4rem;
    color: var(--color-SubBlack);
    text-align: justify;

    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;

    margin: 8px 0px;
`;

const ThreadFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const ThreadFooterLeft = styled.div`
    display: flex;
    gap: 8px;
`;

const ThreadFooterRight = styled.div`
    display: flex;
    gap: 8px;
`;

const ThreadDateText = styled(Text)`
    font-size: 1.2rem;
    color: var(--color-Grey1);
`;

// 깜빡임 애니메이션 정의
const blinkBg = keyframes`
    0% { background-color: var(--color-Base2); }
    50% { background-color: var(--color-White); }
    100% { background-color: var(--color-Base2); }
`;

const MessageContainer = styled.div`
    position: relative;

    padding: 20px 8px;
    margin: 0px 8px; // 사이드바 리사이저 가리는 현상 때문에 margin 추가
    width: calc(100% - 16px);
    height: auto;
    background-color: ${({ bgColor }) => (bgColor ? bgColor : "var(--color-White)")};
    border-bottom: dashed 1px var(--color-Line);
    transition: background-color 80ms ease-in;

    ${({ isBlinking }) =>
        isBlinking &&
        css`
            animation: ${blinkBg} 1s linear 0s 2 alternate;
            /* 1.0s * 2 = 2초 */
        `}
`;

const SourceContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 8px;
`;

const DivideLine = styled.div`
    display: block;
    margin: 8px 0 0 0;
    width: 100%;
    height: 1px;
    background-color: var(--color-Line);
`;

const SourceSubText = styled.div`
    font-size: 10px;
    font-weight: 400;
    border-radius: 4px;
    background-color: var(--color-InfoBox);
    color: var(--color-Grey1);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
        cursor: pointer;
        background-color: var(--color-Base1);
    }
`;

const SourceNumText = styled.span`
    margin: 0 4px 0 4px;
    font-weight: 400;
    color: var(--color-SubBlack);
`;

const MessageHeader = styled.div`
    display: flex;
    justify-content: space-between;

    margin-bottom: 16px;
`;

const MessageHeaderLeft = styled.div`
    display: flex;
    gap: 8px;
`;
const MessageHeaderRight = styled.div`
    display: flex;
    gap: 8px;
`;

const MessageFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const MessageFooterLeft = styled.div`
    display: flex;
    gap: 8px;
`;
const MessageFooterRight = styled.div`
    display: flex;
    gap: 8px;
`;

const MessageVersionText = styled(Text)`
    margin-left: auto;

    display: flex;
    align-items: center;

    font-size: 1.2rem;
    color: var(--color-Grey1);
`;

const ProfileIcon = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 24px;
    width: 24px;
    height: 24px;
    background-color: var(--color-Base1);
    border-radius: 12px;
    border: 1px solid lightgray;
`;

const ProfileText = styled(Text)`
    display: flex;
    align-items: center;

    font-size: 1.2rem;
    color: var(--color-Grey1);
    background-color: var(--color-Background);

    padding: 0 4px;
    border-radius: 4px;
`;

const ProfileIconOverlap = styled.div`
    margin: 0 0 0 -12px;
    &:first-child {
        margin-left: 0;
    }
`;

const MemoBadge = styled(Text)`
    display: flex;
    align-items: center;

    font-size: 1.2rem;
    color: var(--color-Grey1);
    background-color: var(--color-HighlightYellow);

    padding: 0 4px;
    border-radius: 4px;
`;

const MessageDateText = styled(Text)`
    margin-left: auto;

    display: flex;
    align-items: center;

    font-size: 1.2rem;
    color: var(--color-Grey1);
`;

const MessageBody = styled.div`
    margin-bottom: 16px;
`;

const HighlightImage = styled.div`
    position: relative;
    float: left;
    margin-right: 8px;

    width: 48px;
    height: 48px;
    border: 1px solid
        ${({ color }) => {
            return color;
        }};

    overflow: hidden;
`;

const HightlightPageText = styled.div`
    position: absolute;
    inset: 0;

    display: flex;
    justify-content: center;
    align-items: center;

    border-left: 10px solid
        ${({ color }) => {
            return color;
        }};

    opacity: 1;
    color: var(--color-White);
    background-color: var(--color-Black);

    &:hover {
        cursor: pointer;

        opacity: 0.5;
        color: transparent;
        background-color: transparent;
    }
`;

const MessageText = styled(Text)`
    font-size: 1.4rem;
    color: var(--color-SubBlack);
    white-space: pre-line;
    text-align: justify;
`;

const MessageDeletedText = styled(MessageText)`
    color: var(--color-Grey2);
`;

const NoMessageContainer = styled.div`
    padding: 16px;
    color: #888;
`;

function PDFChatMessage(props) {
    const { space_uuid } = useParams();
    const openNewTab = (path) => window.open(`/${space_uuid}${path}`, "_blank", "noopener noreferrer");
    const parseDate = (datetime) => {
        return datetime.split(" ")[0].replaceAll("-", ".");
    };
    const parseTime = (datetime) => {
        return datetime.split(" ")[1].substring(0, "HH:mm".length);
    };

    // region: 포커스 메시지 처리
    const focusedItemRef = useRef();
    const [isFocusedBlinking, setFocusedIsBlinking] = useState(false);
    useEffect(() => {
        if (props.focused) {
            setFocusedIsBlinking(true);
            if (focusedItemRef.current) {
                focusedItemRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            // 3초 후 깜빡임 해제
            const timer = setTimeout(() => setFocusedIsBlinking(false), 2000);
            return () => clearTimeout(timer);
        }
    }, [props.focused]);

    // region: 새창으로 PDF 열기
    const openPDFReview = (page) => {
        const review_window = openNewTab("/reference/" + props.reference.uuid + "/review/" + props.reference.file_uuid);
        const channel = Constants.REVIEW_MESSAGE_CHANNEL;
        const payload = {
            page: page,
            thread_id: props.message.thread,
            message_id: props.message.id,
        };

        // 자식 윈도우와 handshaking 후 메시지 전송
        const readyListener = (event) => {
            if (
                event.source === review_window &&
                event.origin === window.location.origin &&
                event.data?.__channel === channel
            ) {
                review_window.postMessage(
                    {
                        __channel: channel,
                        payload: payload,
                    },
                    window.location.origin,
                );
                window.removeEventListener("message", readyListener);
            }
        };
        window.addEventListener("message", readyListener);
    };

    // region: 스레드, 메시지 수정
    const [isThreadEditing, setIsThreadEditing] = useState(false);
    const [threadTitle, setThreadTitle] = useState(props.thread?.title);
    const threadTitleRef = useRef();
    const handleThreadTitleUpdate = () => {
        if (!threadTitle) {
            return;
        }
        setIsThreadEditing(false);
        props.threadUpdateTitle(props.thread.id, threadTitle);
    };

    const [isMessageEditing, setIsMessageEditing] = useState(false);
    const [editMessageText, setEditMessageText] = useState();
    const editMesssageTextRef = useRef();
    const handleEditMessageTextUpdate = () => {
        if (!editMessageText) {
            return;
        }
        setIsMessageEditing(false);

        let req_message = {
            autoResponse: props.autoResponse,
            content: editMessageText,
            reviewBoxId: props.message.review_box?.id,
            reviewBoxColor: props.message.review_box_color,
            editMessageId: props.message.id,
        };
        props.chatStart(req_message);
    };

    if (!props.message) {
        return <NoMessageContainer />;
    }

    const getMessageAuthorId = (msg) => {
        if (!msg) return 0;
        return msg.space_member || 0;
    };

    // region: 스레드 랜더링
    if (props.isThread) {
        const profile_id_set = new Set();
        let total_messages = 0;
        (props.thread.messages || []).forEach((msg) => {
            if (msg.is_deleted === false) {
                total_messages += 1;
                profile_id_set.add(getMessageAuthorId(msg));
                let match;
                while ((match = matchMention(msg.content || "")) !== null) {
                    profile_id_set.add(Number(match[1]));
                }
            }
        });

        return (
            <ThreadContainer
                bgColor={props.bgColor}
                onClick={() => {
                    props.enterThreadDetailView(props.thread);
                }}
            >
                <ThreadHeader>
                    <ThreadHeaderLeft>
                        {[...profile_id_set].map((id, idx) => {
                            if (id === 0) {
                                // AI: @PREP
                                return (
                                    <ProfileIconOverlap key={"ai-" + idx}>
                                        <Tooltip message="@PREP">
                                            <ProfileIcon>
                                                <Icon name={"aiResponse"} size={"12px"} color={"var(--color-Key)"} />
                                            </ProfileIcon>
                                        </Tooltip>
                                    </ProfileIconOverlap>
                                );
                            } else {
                                // 실제 유저
                                const profile = (props.profiles || []).find((p) => p.id === id);
                                return (
                                    <ProfileIconOverlap key={"profile-" + id}>
                                        <Tooltip message={`@${profile?.name}`}>
                                            <ProfileIcon>
                                                {profile?.image_url ? (
                                                    <img
                                                        src={
                                                            process.env.REACT_APP_ENV === "local"
                                                                ? `http://${process.env.REACT_APP_API_BASE_URL}${profile.image_url}`
                                                                : profile.image_url
                                                        }
                                                        alt={profile?.name}
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            borderRadius: "50%",
                                                        }}
                                                    />
                                                ) : (
                                                    <Icon name={"userFill"} color={"var(--color-SubBlack)"} />
                                                )}
                                            </ProfileIcon>
                                        </Tooltip>
                                    </ProfileIconOverlap>
                                );
                            }
                        })}
                    </ThreadHeaderLeft>
                    <ThreadHeaderRight>
                        <ThreadMessageCountText>{total_messages} messages</ThreadMessageCountText>
                    </ThreadHeaderRight>
                </ThreadHeader>
                {isThreadEditing ? (
                    <MentionsInput
                        autoFocus
                        value={threadTitle}
                        onChange={(event, newValue) => setThreadTitle(newValue)}
                        className="mentions"
                        inputRef={threadTitleRef}
                        placeholder={"스레드 제목을 입력하세요."}
                        allowSuggestionsAboveCursor={true}
                        markup="@\[__display__\](__id__)"
                        singleLine={true}
                        onKeyDown={(e) => {
                            genericEnterKeyDown(e, () => {
                                handleThreadTitleUpdate();
                            });
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={() => {
                            // '저장' 버튼을 누를 수 있기 때문에 약간의 딜레이 추가
                            setTimeout(() => setIsThreadEditing(false), 200);
                        }}
                        style={{ backgroundColor: "white", margin: "16px 0px" }}
                    >
                        <Mention
                            trigger="@"
                            data={mentionCandidates}
                            className="mentions__mention"
                            appendSpaceOnAdd={true}
                            renderSuggestion={(suggestion, search, highlightedDisplay, index, focused) => (
                                <div
                                    style={{
                                        background: focused ? "var(--color-Base2)" : "white",
                                        color: focused ? "black" : "black",
                                    }}
                                >
                                    @{highlightedDisplay}
                                </div>
                            )}
                            displayTransform={(id, display) => `@${display}`}
                        />
                    </MentionsInput>
                ) : (
                    <ThreadTitle
                        dangerouslySetInnerHTML={{
                            __html: props.thread.title.replace(
                                /@\[([^\]]+)\]\(([^)]+)\)/g,
                                `<span style="color: green; font-size: 1.4rem; display: inline-block;">@$1</span>`,
                            ),
                        }}
                    />
                )}
                <ThreadFooter>
                    <ThreadFooterLeft>
                        <ThreadDateText>
                            {parseDate(props.message.created_at) + " " + parseTime(props.message.created_at)}
                        </ThreadDateText>
                    </ThreadFooterLeft>
                    <ThreadFooterRight>
                        {isThreadEditing ? (
                            <Tooltip message={"저장"}>
                                <Button
                                    onlyIcon
                                    bgColor={"transparent"}
                                    hoverBgColor={"var(--color-ButtonHover4)"}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleThreadTitleUpdate();
                                    }}
                                >
                                    <Icon name={"check"} size={"12"} color={"var(--color-Grey1)"} />
                                </Button>
                            </Tooltip>
                        ) : (
                            <Tooltip message={"제목 수정"}>
                                <Button
                                    onlyIcon
                                    bgColor={"transparent"}
                                    hoverBgColor={"var(--color-ButtonHover4)"}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setThreadTitle(props.thread.title);
                                        setIsThreadEditing(true);
                                    }}
                                >
                                    <Icon name={"modify"} size={"12"} color={"var(--color-Grey1)"} />
                                </Button>
                            </Tooltip>
                        )}
                        <Tooltip message={"스레드 삭제"}>
                            <Button
                                onlyIcon
                                bgColor={"transparent"}
                                hoverBgColor={"var(--color-ButtonHover4)"}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    Confirm(
                                        "warn",
                                        "스레드 삭제",
                                        "채팅 스레드를 삭제하면 되돌릴 수 없습니다. 삭제하시겠습니까?",
                                        "삭제",
                                        () => {
                                            props.chatDelete(props.thread.id, null, true);
                                        },
                                    );
                                }}
                            >
                                <Icon name={"delete"} size={"12"} color={"var(--color-Grey1)"} />
                            </Button>
                        </Tooltip>
                    </ThreadFooterRight>
                </ThreadFooter>
            </ThreadContainer>
        );
    }

    // region: 메시지 랜더링
    return (
        <MessageContainer ref={focusedItemRef} isBlinking={isFocusedBlinking}>
            <MessageHeader>
                <MessageHeaderLeft>
                    {props.message.role === Constants.REVIEW_ROLE_HUMAN && (
                        <>
                            <ProfileIcon>
                                {(() => {
                                    const profile = (props.profiles || []).find(
                                        (p) => p.id === getMessageAuthorId(props.message),
                                    );
                                    return profile?.image_url ? (
                                        <img
                                            src={
                                                process.env.REACT_APP_ENV === "local"
                                                    ? `http://${process.env.REACT_APP_API_BASE_URL}${profile.image_url}`
                                                    : profile.image_url
                                            }
                                            alt={profile.name}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                borderRadius: "50%",
                                            }}
                                        />
                                    ) : (
                                        <Icon name={"userFill"} color={"var(--color-SubBlack)"} />
                                    );
                                })()}
                            </ProfileIcon>
                            <ProfileText>
                                {(() => {
                                    const profile = (props.profiles || []).find(
                                        (p) => p.id === getMessageAuthorId(props.message),
                                    );
                                    return profile ? `@${profile.name}` : "";
                                })()}
                            </ProfileText>
                            {!hasPrepMention(props.message.content) && (
                                <MemoBadge>
                                    <Tooltip message="AI 응답 요청(@PREP)이 없는 메시지입니다.">
                                        <span>Memo</span>
                                    </Tooltip>
                                </MemoBadge>
                            )}
                        </>
                    )}
                    {props.message.role === Constants.REVIEW_ROLE_AI && (
                        <>
                            <ProfileIcon>
                                <Icon name={"aiResponse"} size={"12px"} color={"var(--color-Key)"} />
                            </ProfileIcon>
                            <ProfileText>@PREP</ProfileText>
                        </>
                    )}
                </MessageHeaderLeft>
                <MessageHeaderRight>
                    <MessageDateText>
                        {parseDate(props.message.created_at) + " " + parseTime(props.message.created_at)}
                    </MessageDateText>
                </MessageHeaderRight>
            </MessageHeader>

            <MessageBody>
                {isMessageEditing ? (
                    <MentionsInput
                        autoFocus
                        value={editMessageText}
                        onChange={(event, newValue) => setEditMessageText(newValue)}
                        className="mentions"
                        inputRef={editMesssageTextRef}
                        placeholder={"메시지 내용을 입력하세요."}
                        allowSuggestionsAboveCursor={true}
                        markup="@\[__display__\](__id__)"
                        singleLine={false}
                        onKeyDown={(e) => {
                            genericEnterKeyDown(e, () => {
                                if (e.shiftKey) {
                                    setEditMessageText((prev) => prev + "\n");
                                } else {
                                    handleEditMessageTextUpdate();
                                }
                            });
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={() => {
                            // '저장' 버튼을 누를 수 있기 때문에 약간의 딜레이 추가
                            setTimeout(() => setIsMessageEditing(false), 200);
                        }}
                        style={{ backgroundColor: "var(--color-Base1)", margin: "16px 0px" }}
                    >
                        <Mention
                            trigger="@"
                            data={mentionCandidates}
                            className="mentions__mention"
                            appendSpaceOnAdd={true}
                            renderSuggestion={(suggestion, search, highlightedDisplay, index, focused) => (
                                <div
                                    style={{
                                        background: focused ? "var(--color-Base2)" : "white",
                                        color: focused ? "black" : "black",
                                    }}
                                >
                                    @{highlightedDisplay}
                                </div>
                            )}
                            displayTransform={(id, display) => `@${display}`}
                        />
                    </MentionsInput>
                ) : (
                    <>
                        {props.message.is_deleted ? (
                            <MessageDeletedText>삭제된 메시지입니다.</MessageDeletedText>
                        ) : (
                            <>
                                {props.message.review_box && props.message.review_box_image && (
                                    <HighlightImage
                                        color={props.message.review_box_color}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (props.setCurPage && props.setSearchBox) {
                                                // ReferenceReviewPage 에서 접근한 경우
                                                props.setCurPage(props.message.review_box.page);
                                                props.setSearchBox({ ...props.message.review_box });
                                            } else {
                                                // ReferenceDetailPage 에서 접근한 경우
                                                openPDFReview(props.message.review_box.page);
                                            }
                                        }}
                                    >
                                        <HightlightPageText color={props.message.review_box_color}>
                                            p.{props.message.review_box.page}
                                        </HightlightPageText>
                                        <img
                                            src={"data:image/png;base64," + props.message.review_box_image.base64}
                                            style={{
                                                width: props.message.review_box_image.width / 3,
                                                height: props.message.review_box_image.height / 3,
                                                // maxWidth: "800px",
                                                // maxHeight: "800px",
                                            }}
                                        />
                                    </HighlightImage>
                                )}

                                <MessageText>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: props.message.content.replace(
                                                /@\[([^\]]+)\]\(([^)]+)\)/g,
                                                `<span style="color: green; font-size: 1.4rem; display: inline-block;">@$1</span>`,
                                            ),
                                        }}
                                    />
                                    {props.streamingMessageIds &&
                                        props.streamingMessageIds.includes(props.message.id) && <AiCursor />}
                                </MessageText>

                                {/* 출처 */}
                                {props.message.sources && props.message.sources.length > 0 && (
                                    <>
                                        <DivideLine />
                                        <SourceContent>
                                            {props.message.sources.map((source, index) => {
                                                return (
                                                    <SourceSubText
                                                        key={index}
                                                        onClick={(e) => {
                                                            if (props.setCurPage) {
                                                                // ReferenceReviewPage 에서 접근한 경우
                                                                props.setCurPage(source.source_page);
                                                            } else {
                                                                // ReferenceDetailPage 에서 접근한 경우
                                                                openPDFReview(source.source_page);
                                                            }
                                                        }}
                                                    >
                                                        <SourceNumText>p.{source.source_page}</SourceNumText>
                                                        {source.source_text}
                                                    </SourceSubText>
                                                );
                                            })}
                                        </SourceContent>
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}
            </MessageBody>

            <MessageFooter>
                {props.versionCount ? (
                    // 검색된 메시지가 아니면, props.versionCount 가 있음
                    <>
                        <MessageFooterLeft>
                            <Tooltip message={"이전 버전"}>
                                <Button
                                    onlyIcon
                                    bgColor={"transparent"}
                                    hoverBgColor={"var(--color-ButtonHover4)"}
                                    onClick={() => {
                                        if (props.message.version > 1) {
                                            props.onChangeVersion(-1);
                                        }
                                    }}
                                >
                                    <Icon
                                        name={"arrowLeft"}
                                        size={"10"}
                                        color={props.message.version > 1 ? "var(--color-Grey1)" : "var(--color-Grey3)"}
                                    />
                                </Button>
                            </Tooltip>
                            <MessageVersionText>
                                {props.message.version} / {props.versionCount}
                            </MessageVersionText>
                            <Tooltip message={"다음 버전"}>
                                <Button
                                    onlyIcon
                                    bgColor={"transparent"}
                                    hoverBgColor={"var(--color-ButtonHover4)"}
                                    onClick={() => {
                                        if (props.message.version < props.versionCount) {
                                            props.onChangeVersion(1);
                                        }
                                    }}
                                >
                                    <Icon
                                        name={"arrowRight"}
                                        size={"10"}
                                        color={
                                            props.message.version < props.versionCount
                                                ? "var(--color-Grey1)"
                                                : "var(--color-Grey3)"
                                        }
                                    />
                                </Button>
                            </Tooltip>
                        </MessageFooterLeft>
                        <MessageFooterRight>
                            {props.message.role === Constants.REVIEW_ROLE_HUMAN && (
                                <>
                                    {props.message.is_deleted ? (
                                        <>
                                            {isMessageEditing ? (
                                                <Tooltip message={"저장"}>
                                                    <Button
                                                        onlyIcon
                                                        bgColor={"transparent"}
                                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEditMessageTextUpdate();
                                                        }}
                                                    >
                                                        <Icon name={"check"} size={"12"} color={"var(--color-Grey1)"} />
                                                    </Button>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip message={"메시지 재작성"}>
                                                    <Button
                                                        onlyIcon
                                                        bgColor={"transparent"}
                                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                                        onClick={() => {
                                                            setEditMessageText(props.message.content);
                                                            setIsMessageEditing(true);
                                                        }}
                                                    >
                                                        <Icon
                                                            name={"modify"}
                                                            size={"12"}
                                                            color={"var(--color-Grey3)"}
                                                        />
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            <Tooltip message={"메시지 복원"}>
                                                <Button
                                                    onlyIcon
                                                    bgColor={"transparent"}
                                                    hoverBgColor={"var(--color-ButtonHover4)"}
                                                    onClick={() => {
                                                        Confirm(
                                                            "warn",
                                                            "메시지 복원",
                                                            "이 메시지와 기존 하위 메시지를 복원하시겠습니까?",
                                                            "복원",
                                                            () => {
                                                                props.chatDelete(
                                                                    props.thread.id,
                                                                    props.message.id,
                                                                    true,
                                                                );
                                                            },
                                                        );
                                                    }}
                                                >
                                                    <Icon name={"refresh"} size={"12"} color={"var(--color-Grey1)"} />
                                                </Button>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <>
                                            {isMessageEditing ? (
                                                <Tooltip message={"저장"}>
                                                    <Button
                                                        onlyIcon
                                                        bgColor={"transparent"}
                                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEditMessageTextUpdate();
                                                        }}
                                                    >
                                                        <Icon name={"check"} size={"12"} color={"var(--color-Grey1)"} />
                                                    </Button>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip message={"매시지 재작성"}>
                                                    <Button
                                                        onlyIcon
                                                        bgColor={"transparent"}
                                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                                        onClick={() => {
                                                            setEditMessageText(props.message.content);
                                                            setIsMessageEditing(true);
                                                        }}
                                                    >
                                                        <Icon
                                                            name={"modify"}
                                                            size={"12"}
                                                            color={"var(--color-Grey1)"}
                                                        />
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            {getMessageAuthorId(props.message) === props.myProfileId && (
                                                <Tooltip message={"메시지 삭제"}>
                                                    <Button
                                                        onlyIcon
                                                        bgColor={"transparent"}
                                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                                        onClick={() => {
                                                            Confirm(
                                                                "warn",
                                                                "메시지 삭제",
                                                                "메시지를 삭제하시겠습니까?\n하위 메시지도 볼 수 없게 됩니다.",
                                                                "삭제",
                                                                () => {
                                                                    props.chatDelete(
                                                                        props.thread.id,
                                                                        props.message.id,
                                                                        false,
                                                                    );
                                                                },
                                                            );
                                                        }}
                                                    >
                                                        <Icon
                                                            name={"delete"}
                                                            size={"12"}
                                                            color={"var(--color-Grey1)"}
                                                        />
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                            {props.message.role === Constants.REVIEW_ROLE_AI && (
                                <>
                                    <Tooltip message={"재응답"}>
                                        <Button
                                            onlyIcon
                                            bgColor={"transparent"}
                                            hoverBgColor={"var(--color-ButtonHover4)"}
                                            onClick={() => {
                                                let req_message = {
                                                    editMessageId: props.message.id,
                                                };
                                                props.chatStart(req_message);
                                            }}
                                        >
                                            <Icon name={"refresh"} size={"12"} color={"var(--color-Grey1)"} />
                                        </Button>
                                    </Tooltip>
                                </>
                            )}
                        </MessageFooterRight>
                    </>
                ) : (
                    // 검색된 메시지는 props.versionCount 가 없고, footer 를 비워둠
                    <></>
                )}
            </MessageFooter>
        </MessageContainer>
    );
}

export default PDFChatMessage;
